/* eslint-disable no-param-reassign */
import { PayloadAction, createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { API_URLS } from '../../constants/ApiUrls';
import { eosGet } from '../../services/EosApi';
import {
  IEOSPolicy,
  IEOSUser,
  IEOSUserState,
  IEOSUserStateShape,
} from '../types/eosUser.types';

export const initialState: IEOSUserState = {
  isFetching: false,
  user: null,
  permissions: [],
  error: null,
};

export const fetchEOSUserPermissions = createAsyncThunk(
  'eosUser/fetchEOSUserPermissions',
  async (_, { rejectWithValue }) => {
    try {
      const response = await eosGet(API_URLS.EOS_USER_SELF);
      const data = response.data;

      return {
        user: data?.user as IEOSUser,
        permissions:
          data?.policies?.map((item: IEOSPolicy) => item.policyName) ?? [],
      };
    } catch (error) {
      return rejectWithValue(
        error instanceof Error ? error.message : 'Unknown error occurred'
      );
    }
  }
);

const eosUserSlice = createSlice({
  name: 'eosUser',
  initialState,
  reducers: {
    resetEOSUser: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchEOSUserPermissions.pending, (state) => {
        state.isFetching = true;
        state.error = null;
      })
      .addCase(
        fetchEOSUserPermissions.fulfilled,
        (
          state,
          { payload }: PayloadAction<{ user: IEOSUser; permissions: string[] }>
        ) => {
          state.isFetching = false;
          state.user = payload.user;
          state.permissions = payload.permissions;
          state.error = null;
        }
      )
      .addCase(fetchEOSUserPermissions.rejected, (state, action) => {
        state.isFetching = false;
        state.error = action.payload as string;
      });
  },
});

export const { resetEOSUser } = eosUserSlice.actions;
export default eosUserSlice.reducer;

export const selectEOSUser = (state: IEOSUserStateShape) => state.eosUser;
export const selectEOSPermissions = (state: IEOSUserStateShape) =>
  state.eosUser.permissions;
